import { Canceler } from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useHistory, useParams } from 'react-router-dom';
import { FixedTab, RouteTabs } from '../../components/RouteTabs';
import useBackdrop from '../../hooks/useBackdrop';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import useSnackbar from '../../hooks/useSnackbar';
import api from '../../services/api/base';
import { IFreeStorySchedule } from '../../services/types';
import { RouteNames } from '../config';
import FreeStoryScheduleList from './FreeStoryScheduleList';
import FreeStoryScheduleView from './FreeStoryScheduleView';
import { getFreeStorySchedules, deleteFreeStorySchedule } from '../../services/api/FreeStorySchedule';

interface IProps {}

const FreeStory = (props: IProps & RouteComponentProps): JSX.Element => {
  const path = RouteNames.FreeStoryScheduling;
  const { freeStoryScheduleId } = useParams<{ freeStoryScheduleId: string }>();

  const history = useHistory();

  const initialfreeStoryScheduleIds = freeStoryScheduleId ? [parseInt(freeStoryScheduleId)] : [];

  const [schedules, setSchedules] = useState<IFreeStorySchedule[]>([]);

  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();

  const canceler = useRef<Canceler>(() => undefined);

  const onError = useCallback(
    e => {
      snackbar.error(`An error has occurred. ${e.message}`);
    },
    [snackbar],
  );

  useEffect(() => {
    const fetchData = async () => {
      canceler.current();

      try {
        const [fetchPromise, cancel] = await getFreeStorySchedules({});
        canceler.current = cancel;
        const result = await fetchPromise;

        setSchedules(result.data);
      } catch (e) {
        if (api.isCancel(e)) return;
      }
    };

    fetchData();

    return () => canceler.current();
  }, []);

  const handleDeleteClick = useCallback(
    sc => {
      const onDelete = (freeStoryScheduleId: number) => {
        const onProceed = async () => {
          if (!freeStoryScheduleId) return;

          const idx = schedules.findIndex(rc => rc.id === freeStoryScheduleId);

          try {
            backdrop.show();
            const result = await deleteFreeStorySchedule(freeStoryScheduleId);

            if (result.status === 200) {
              const cloned = [...schedules];
              cloned.splice(idx, 1);

              setSchedules(cloned);

              snackbar.success('Deleted successfully.');
            }
          } catch (e) {
            console.error(e);
            snackbar.error(`An error has occurred. ${e.message}`);
          } finally {
            backdrop.hide();
          }
        };

        confirm.open({
          title: 'Delete',
          text: 'Are you sure you want to delete this schedule?',
          onProceed,
        });
      };

      onDelete(sc.id);
    },
    [schedules, backdrop, confirm, snackbar],
  );

  const handleCreate = useCallback(
    data => {
      if (schedules) {
        setSchedules([data, ...schedules]);
      }

      history.push(RouteNames.FreeStoryScheduling);

      snackbar.success('Saved successfully.');
    },
    [schedules, history, snackbar],
  );

  return (
    <RouteTabs path={path}>
      <FixedTab label="All Schedules">
        <FreeStoryScheduleList schedules={schedules} onClickDeleteButton={handleDeleteClick} />
      </FixedTab>
      <FixedTab label="Create a new schedule" uri="create">
        <FreeStoryScheduleView onCreated={handleCreate} onError={onError} />
      </FixedTab>
    </RouteTabs>
  );
};

export default FreeStory;
